/**
 * Toast Notification System
 * Provides user-friendly notifications instead of browser alerts
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
}

interface ToastContextType {
    toasts: Toast[];
    showToast: (message: string, type?: ToastType, duration?: number) => void;
    removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Toast colors based on type
const toastStyles: Record<ToastType, { background: string; border: string; icon: string }> = {
    success: {
        background: 'rgba(34, 197, 94, 0.15)',
        border: 'rgba(34, 197, 94, 0.5)',
        icon: '✓'
    },
    error: {
        background: 'rgba(239, 68, 68, 0.15)',
        border: 'rgba(239, 68, 68, 0.5)',
        icon: '✕'
    },
    warning: {
        background: 'rgba(251, 191, 36, 0.15)',
        border: 'rgba(251, 191, 36, 0.5)',
        icon: '⚠'
    },
    info: {
        background: 'rgba(59, 130, 246, 0.15)',
        border: 'rgba(59, 130, 246, 0.5)',
        icon: 'ℹ'
    }
};

const iconColors: Record<ToastType, string> = {
    success: '#22c55e',
    error: '#ef4444',
    warning: '#fbbf24',
    info: '#3b82f6'
};

/**
 * Individual Toast Component
 */
const ToastItem: React.FC<{
    toast: Toast;
    onRemove: (id: string) => void;
}> = ({ toast, onRemove }) => {
    const [isExiting, setIsExiting] = useState(false);
    const style = toastStyles[toast.type];

    useEffect(() => {
        const duration = toast.duration || 4000;
        const exitTimer = setTimeout(() => setIsExiting(true), duration - 300);
        const removeTimer = setTimeout(() => onRemove(toast.id), duration);

        return () => {
            clearTimeout(exitTimer);
            clearTimeout(removeTimer);
        };
    }, [toast.id, toast.duration, onRemove]);

    return (
        <div
            role="alert"
            aria-live="polite"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem 1.25rem',
                background: style.background,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${style.border}`,
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                color: 'white',
                fontSize: '0.95rem',
                maxWidth: '400px',
                animation: isExiting ? 'slideOut 0.3s ease-out forwards' : 'slideIn 0.3s ease-out',
                cursor: 'pointer'
            }}
            onClick={() => {
                setIsExiting(true);
                setTimeout(() => onRemove(toast.id), 300);
            }}
        >
            <span style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: style.border,
                color: iconColors[toast.type],
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0
            }}>
                {style.icon}
            </span>
            <span style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</span>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setIsExiting(true);
                    setTimeout(() => onRemove(toast.id), 300);
                }}
                aria-label="Dismiss notification"
                style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.5)',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    fontSize: '1.25rem',
                    lineHeight: 1,
                    transition: 'color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = 'white'}
                onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
            >
                ×
            </button>
        </div>
    );
};

/**
 * Toast Container - renders all active toasts
 */
const ToastContainer: React.FC<{ toasts: Toast[]; onRemove: (id: string) => void }> = ({
    toasts,
    onRemove
}) => {
    if (toasts.length === 0) return null;

    return (
        <div
            aria-label="Notifications"
            style={{
                position: 'fixed',
                top: '1rem',
                right: '1rem',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                pointerEvents: 'none'
            }}
        >
            {toasts.map((toast) => (
                <div key={toast.id} style={{ pointerEvents: 'auto' }}>
                    <ToastItem toast={toast} onRemove={onRemove} />
                </div>
            ))}
        </div>
    );
};

/**
 * Toast Provider Component
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType = 'info', duration?: number) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setToasts((prev) => [...prev, { id, message, type, duration }]);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
};

/**
 * Hook to use toast notifications
 */
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export default ToastProvider;
